import { useAuthStore } from '~/stores/auth.store';
import type {
  ArchivedBonusStack,
  CasinoBonus,
  CasinoBonusStackResponse,
  IForfeitCasinoBonusResponse,
} from '~/interfaces/dto/bonus/response';

export const useBonusService = () => {
  const { $fetch } = useNuxtApp();
  const authStore = useAuthStore();
  const tld = 'co.za';

  const getShowBonusBalance = async (): Promise<boolean> => {
    return await $fetch<boolean>(
      `${tld}/casinobonusing/api/v2/CasinoBonus/showbonusbalance/${authStore.userId}`,
      {
        base: 'casinoBonus',
        useBrandId: true,
        useAuthentication: true,
        retry: false,
        headers: {
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
        },
        onResponse({ response }) {
          authStore.displayBonusBalance = response._data;
        },
        onResponseError() {
          console.error(`Couldn't fetch player bonus`);
        },
      },
    );
  };

  const getActiveBonus = async (): Promise<CasinoBonus> => {
    if (!authStore.userId) return;
    try {
      return await $fetch<CasinoBonus>(
        `${tld}/casinobonusing/api/v2/CasinoBonus/active/${authStore.userId}`,
        {
          base: 'casinoBonus',
          useBrandId: true,
          useAuthentication: true,
          retry: false,
          headers: {
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json',
          },
          onResponse({ response }) {
            authStore.setActiveBonus(response._data);
          },
          onResponseError() {
            console.error(`Couldn't fetch player bonus`);
          },
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const getBonusStack = async (): Promise<CasinoBonusStackResponse> => {
    try {
      return await $fetch<CasinoBonusStackResponse>(
        `${tld}/casinobonusing/api/v2/CasinoBonus/player/${authStore.user_id}/stack`,
        {
          base: 'casinoBonus',
          useBrandId: true,
          useAuthentication: true,
          headers: {
            'Cache-Control': 'no-cache',
          },
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const getArchiveBonusStack = async ({
    fromDate,
    toDate,
  }: {
    fromDate: string | Date;
    toDate: string | Date;
  }): Promise<ArchivedBonusStack> => {
    try {
      return await $fetch<ArchivedBonusStack>(
        `${tld}/casinobonusing/api/v2/CasinoBonus/player/${authStore.user_id}/stack/archived`,
        {
          base: 'casinoBonus',
          useBrandId: true,
          useAuthentication: true,
          headers: {
            'Cache-Control': 'no-cache',
          },
          query: {
            fromDate,
            toDate,
          },
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const forfeitBonus = async (
    casinoBonusId: string,
  ): Promise<IForfeitCasinoBonusResponse> => {
    try {
      return await $fetch<IForfeitCasinoBonusResponse>(
        `${tld}/casinobonusing/api/v2/CasinoBonus/player/${authStore.user_id}/forfeit/${casinoBonusId}`,
        {
          method: 'post',
          base: 'casinoBonus',
          useBrandId: true,
          useAuthentication: true,
          headers: {
            'Cache-Control': 'no-cache',
          },
          body: null,
          query: {
            casinoBonusId,
          },
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  return {
    getBonusStack,
    getArchiveBonusStack,
    forfeitBonus,
    getActiveBonus,
    getShowBonusBalance,
  };
};
